import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Star, MapPin, Briefcase } from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsultancyHub = () => {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('ALL');
  const [availOnly, setAvailOnly] = useState(false);

  useEffect(() => {
    axios.get(`${API}/consultants`).then((r) => setList(r.data)).catch(() => setList([]));
    axios.get(`${API}/consultants/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const cities = useMemo(() => ['ALL', ...Object.keys(stats?.cities || {})], [stats]);

  const filtered = useMemo(() => {
    return list
      .filter((c) => (city === 'ALL' ? true : c.city === city))
      .filter((c) => (availOnly ? c.available : true))
      .filter((c) => {
        const s = q.trim().toLowerCase();
        if (!s) return true;
        return [c.name, c.specialisation, c.city].join(' ').toLowerCase().includes(s);
      });
  }, [list, city, availOnly, q]);

  return (
    <EmbeddedModule
      eyebrow="Marketplace · Pan-India"
      title="Consultancy Hub"
      tagline="Curated database of regulatory, audit, AML, AI-governance and litigation specialists — discoverable, available and bookable from one console."
      badgeText="India · 8 cities"
      accentClass="from-violet-700 via-fuchsia-700 to-pink-700"
      testId="consultancy-hub-page"
      kpis={[
        { label: 'Consultants', value: stats?.total ?? '—' },
        { label: 'Available Now', value: stats?.available ?? '—' },
        { label: 'Cities', value: stats ? Object.keys(stats.cities).length : '—' },
        { label: 'Avg. Rating', value: '4.78 / 5' }
      ]}
    >
      <Card data-testid="consultancy-hub-list">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Browse Specialists</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, specialisation..."
                className="h-9 w-64"
                data-testid="consultant-search"
              />
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="consultant-city"
                className="h-9 px-3 border border-gray-300 rounded-md text-sm"
              >
                {cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <label className="text-xs text-gray-700 flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={availOnly}
                  onChange={(e) => setAvailOnly(e.target.checked)}
                  data-testid="consultant-avail-only"
                />
                Available only
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`consultant-${c.id}`}
                className="border border-gray-200 rounded-xl p-4 bg-white hover:border-violet-400 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center font-semibold">
                    {c.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                  </div>
                  <Badge
                    className={c.available
                      ? 'bg-emerald-100 text-emerald-700 text-[10px]'
                      : 'bg-gray-100 text-gray-500 text-[10px]'}
                  >
                    {c.available ? 'Available' : 'Engaged'}
                  </Badge>
                </div>
                <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                <p className="text-xs text-gray-600 mt-0.5">{c.specialisation}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-2">
                  <span className="inline-flex items-center"><MapPin className="w-3 h-3 mr-1" />{c.city}</span>
                  <span className="inline-flex items-center"><Briefcase className="w-3 h-3 mr-1" />{c.experience_years}y</span>
                  <span className="inline-flex items-center text-amber-600">
                    <Star className="w-3 h-3 mr-1 fill-current" />{c.rating}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(c.languages || []).map((l) => (
                    <Badge key={l} variant="outline" className="text-[10px]">{l}</Badge>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-3">{c.engagements} engagements completed</p>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">No consultants match.</p>
          )}
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default ConsultancyHub;
